export interface PageNode {
  name: string;
  path: string;
  component?: string;
  permissionKey?: string; // Key used for permission checking
  children?: PageNode[];
}

// Permission keys that can be assigned to users - child level granular permissions
export const PAGE_PERMISSION_KEYS = [
  // Dashboard
  'view_dashboard',
  // Customers (includes Shipments)
  'customers_list',
  'customers_create',
  'customers_edit',
  'customers_orders',
  'customers_order_view',
  'customers_inbound_create',
  'customers_outbound_create',
  'shipments_list',
  'shipments_view',
  // Suppliers
  'suppliers_list',
  'suppliers_create',
  'suppliers_edit',
  'suppliers_orders',
  'suppliers_order_view',
  'suppliers_inbound_create',
  // Items (includes Inventory)
  'items_list',
  'items_create',
  'items_edit',
  'items_detail',
  'inventory_list',
  // Categories
  'categories_list',
  'categories_create',
  'categories_edit',
  // Users
  'users_list',
  'users_create',
  'users_edit',
  'users_item_limits',
  // Reports
  'reports_lapsed',
  'reports_inventory_movement',
  'reports_sales_graph',
  'reports_pending_order_items',
] as const;

export type PagePermissionKey = typeof PAGE_PERMISSION_KEYS[number];

export type PagePermissions = Record<PagePermissionKey, boolean>;

export const DEFAULT_PAGE_PERMISSIONS: PagePermissions = {
  // Dashboard - enabled by default
  view_dashboard: true,
  // Customers (includes Shipments)
  customers_list: false,
  customers_create: false,
  customers_edit: false,
  customers_orders: false,
  customers_order_view: false,
  customers_inbound_create: false,
  customers_outbound_create: false,
  shipments_list: false,
  shipments_view: false,
  // Suppliers
  suppliers_list: false,
  suppliers_create: false,
  suppliers_edit: false,
  suppliers_orders: false,
  suppliers_order_view: false,
  suppliers_inbound_create: false,
  // Items (includes Inventory)
  items_list: false,
  items_create: false,
  items_edit: false,
  items_detail: false,
  inventory_list: false,
  // Categories
  categories_list: false,
  categories_create: false,
  categories_edit: false,
  // Users
  users_list: false,
  users_create: false,
  users_edit: false,
  users_item_limits: false,
  // Reports
  reports_lapsed: false,
  reports_inventory_movement: false,
  reports_sales_graph: false,
  reports_pending_order_items: false,
};

export const PAGE_PERMISSION_LABELS: Record<PagePermissionKey, string> = {
  view_dashboard: 'View Dashboard',
  // Customers (includes Shipments)
  customers_list: 'View Customer List',
  customers_create: 'Create Customer',
  customers_edit: 'Edit Customer',
  customers_orders: 'View Customer Orders',
  customers_order_view: 'View Order Details',
  customers_inbound_create: 'Create Inbound Order',
  customers_outbound_create: 'Create Outbound Order',
  shipments_list: 'View Shipments',
  shipments_view: 'View Shipment Details',
  // Suppliers
  suppliers_list: 'View Supplier List',
  suppliers_create: 'Create Supplier',
  suppliers_edit: 'Edit Supplier',
  suppliers_orders: 'View Supplier Orders',
  suppliers_order_view: 'View Order Details',
  suppliers_inbound_create: 'Create Inbound Order',
  // Items (includes Inventory)
  items_list: 'View Items List',
  items_create: 'Create Item',
  items_edit: 'Edit Item',
  items_detail: 'View Item Details',
  inventory_list: 'View Stock List',
  // Categories
  categories_list: 'View Categories',
  categories_create: 'Create Category',
  categories_edit: 'Edit Category',
  // Users
  users_list: 'View Users',
  users_create: 'Create User',
  users_edit: 'Edit User',
  users_item_limits: 'Manage Item Limits',
  // Reports
  reports_lapsed: 'View Lapsed Users Report',
  reports_inventory_movement: 'View Inventory Movement Report',
  reports_sales_graph: 'View Sales Graph Report',
  reports_pending_order_items: "View Pending Order Item Reports"
};

// Group permissions by section for UI display
export interface PermissionSection {
  name: string;
  keys: PagePermissionKey[];
}

export const PERMISSION_SECTIONS: PermissionSection[] = [
  { name: 'Dashboard', keys: ['view_dashboard'] },
  { name: 'Customers', keys: ['customers_list', 'customers_create', 'customers_edit', 'customers_orders', 'customers_order_view', 'customers_inbound_create', 'customers_outbound_create', 'shipments_list', 'shipments_view'] },
  { name: 'Suppliers', keys: ['suppliers_list', 'suppliers_create', 'suppliers_edit', 'suppliers_orders', 'suppliers_order_view', 'suppliers_inbound_create'] },
  { name: 'Items', keys: ['items_list', 'items_create', 'items_edit', 'items_detail', 'inventory_list'] },
  { name: 'Reports', keys: ['reports_lapsed', 'reports_inventory_movement', 'reports_sales_graph', 'reports_pending_order_items'] },
];

export const PAGES: PageNode[] = [
  {
    name: 'Dashboard',
    path: '/dashboard',
    component: 'Dashboard',
    permissionKey: 'view_dashboard',
  },
  {
    name: 'Customers',
    path: '/customers',
    children: [
      {
        name: 'Customer List',
        path: '/customers',
        component: 'CustomerList',
        permissionKey: 'customers_list',
      },
      {
        name: 'Customer Form',
        path: '/customers/new',
        component: 'CustomerForm',
        permissionKey: 'customers_create',
      },
      {
        name: 'Customer Edit',
        path: '/customers/:id/edit',
        component: 'CustomerForm',
        permissionKey: 'customers_edit',
      },
      {
        name: 'Customer Orders',
        path: '/customers/:id/orders',
        component: 'CustomerOrders',
        permissionKey: 'customers_orders',
      },
      {
        name: 'Customer Order View',
        path: '/customers/:id/orders/:orderId',
        component: 'CustomerOrderView',
        permissionKey: 'customers_order_view',
      },
      {
        name: 'Inbound Order Create',
        path: '/customers/:id/orders/inbound/new',
        component: 'InboundOrderCreate',
        permissionKey: 'customers_inbound_create',
      },
      {
        name: 'Outbound Order Create',
        path: '/customers/:id/orders/outbound/new',
        component: 'OutboundOrderCreate',
        permissionKey: 'customers_outbound_create',
      },
      {
        name: 'Shipment List',
        path: '/shipments',
        component: 'ShipmentList',
        permissionKey: 'shipments_list',
      },
      {
        name: 'Shipment View',
        path: '/shipments/:id',
        component: 'ShipmentView',
        permissionKey: 'shipments_view',
      },
    ],
  },
  {
    name: 'Suppliers',
    path: '/suppliers',
    children: [
      {
        name: 'Supplier List',
        path: '/suppliers',
        component: 'SupplierList',
        permissionKey: 'suppliers_list',
      },
      {
        name: 'Supplier Form',
        path: '/suppliers/new',
        component: 'SupplierForm',
        permissionKey: 'suppliers_create',
      },
      {
        name: 'Supplier Edit',
        path: '/suppliers/:id/edit',
        component: 'SupplierForm',
        permissionKey: 'suppliers_edit',
      },
      {
        name: 'Supplier Orders',
        path: '/suppliers/:id/orders',
        component: 'SupplierOrders',
        permissionKey: 'suppliers_orders',
      },
      {
        name: 'Supplier Order View',
        path: '/suppliers/:id/orders/:orderId',
        component: 'SupplierOrderView',
        permissionKey: 'suppliers_order_view',
      },
      {
        name: 'Supplier Inbound Order Create',
        path: '/suppliers/:id/orders/inbound/new',
        component: 'SupplierInboundOrderCreate',
        permissionKey: 'suppliers_inbound_create',
      },
    ],
  },
  {
    name: 'Items',
    path: '/items',
    children: [
      {
        name: 'Items List',
        path: '/items',
        component: 'ItemsList',
        permissionKey: 'items_list',
      },
      {
        name: 'Item Form',
        path: '/items/new',
        component: 'ItemForm',
        permissionKey: 'items_create',
      },
      {
        name: 'Item Edit',
        path: '/items/:id/edit',
        component: 'ItemForm',
        permissionKey: 'items_edit',
      },
      {
        name: 'Item Detail',
        path: '/items/:id',
        component: 'ItemDetail',
        permissionKey: 'items_detail',
      },
      {
        name: 'Stock List',
        path: '/inventory',
        component: 'StockList',
        permissionKey: 'inventory_list',
      },
    ],
  },
  {
    name: 'Users',
    path: '/users',
    children: [
      {
        name: 'User List',
        path: '/users',
        component: 'UserList',
        permissionKey: 'users_list',
      },
      {
        name: 'User Form',
        path: '/users/new',
        component: 'UserForm',
        permissionKey: 'users_create',
      },
      {
        name: 'User Edit',
        path: '/users/:id/edit',
        component: 'UserForm',
        permissionKey: 'users_edit',
      },
      {
        name: 'User Item Limits',
        path: '/users/:id/item-limits',
        component: 'UserItemLimits',
        permissionKey: 'users_item_limits',
      },
    ],
  },
];

// Flat list of all pages for easy lookup
export const getAllPages = (): PageNode[] => {
  const flatList: PageNode[] = [];
  
  const flatten = (nodes: PageNode[]) => {
    nodes.forEach(node => {
      if (node.component) {
        flatList.push(node);
      }
      if (node.children) {
        flatten(node.children);
      }
    });
  };
  
  flatten(PAGES);
  return flatList;
};

// Get page by path
export const getPageByPath = (path: string): PageNode | undefined => {
  return getAllPages().find(page => page.path === path);
};

// Get parent page
export const getParentPage = (path: string): PageNode | undefined => {
  return PAGES.find(page => 
    page.children?.some(child => child.path === path)
  );
};

// Get permission key for a given path
export const getPermissionKeyForPath = (path: string): PagePermissionKey | null => {
  // Check top-level pages with permission
  for (const page of PAGES) {
    if (page.permissionKey && page.path === path) {
      return page.permissionKey as PagePermissionKey;
    }

    // Check children
    if (page.children) {
      for (const child of page.children) {
        if (child.permissionKey) {
          // Exact match
          if (child.path === path) {
            return child.permissionKey as PagePermissionKey;
          }
          // Convert route params to regex pattern for dynamic routes
          const pattern = child.path.replace(/:[^/]+/g, '[^/]+');
          const regex = new RegExp(`^${pattern}$`);
          if (regex.test(path)) {
            return child.permissionKey as PagePermissionKey;
          }
        }
      }
    }
  }

  return null;
};

/**
 * Check if user has permission to access a page.
 * If the page is not defined in PAGES (was removed), returns true by default.
 * @param path - The route path to check
 * @param userPermissions - User's permission object
 * @returns true if user has access, false otherwise
 */
export const hasPagePermission = (
  path: string,
  userPermissions: Partial<PagePermissions>
): boolean => {
  const permissionKey = getPermissionKeyForPath(path);
  
  // If page is not in PAGES (no permission key found), grant access by default
  if (!permissionKey) {
    return true;
  }
  
  // Check if user has the required permission
  return userPermissions[permissionKey] === true;
};

// Get all pages that require a specific permission
export const getPagesByPermission = (permissionKey: PagePermissionKey): PageNode[] => {
  const result: PageNode[] = [];
  
  PAGES.forEach(page => {
    if (page.permissionKey === permissionKey) {
      result.push(page);
    }
    if (page.children) {
      page.children.forEach(child => {
        if (child.permissionKey === permissionKey) {
          result.push(child);
        }
      });
    }
  });
  
  return result;
};

// Check if all permissions in a section are enabled
export const isSectionFullyEnabled = (
  section: PermissionSection,
  permissions: Partial<PagePermissions>
): boolean => {
  return section.keys.every(key => permissions[key] === true);
};

// Check if any permission in a section is enabled
export const isSectionPartiallyEnabled = (
  section: PermissionSection,
  permissions: Partial<PagePermissions>
): boolean => {
  return section.keys.some(key => permissions[key] === true) && 
         !section.keys.every(key => permissions[key] === true);
};

// Nested permissions structure for API
export interface NestedPermissions {
  dashboard: {
    view_dashboard: boolean;
  };
  customers: {
    customers_list: boolean;
    customers_create: boolean;
    customers_edit: boolean;
    customers_orders: boolean;
    customers_order_view: boolean;
    customers_inbound_create: boolean;
    customers_outbound_create: boolean;
    shipments_list: boolean;
    shipments_view: boolean;
  };
  suppliers: {
    suppliers_list: boolean;
    suppliers_create: boolean;
    suppliers_edit: boolean;
    suppliers_orders: boolean;
    suppliers_order_view: boolean;
    suppliers_inbound_create: boolean;
  };
  items: {
    items_list: boolean;
    items_create: boolean;
    items_edit: boolean;
    items_detail: boolean;
    inventory_list: boolean;
  };
  users: {
    users_list: boolean;
    users_create: boolean;
    users_edit: boolean;
    users_item_limits: boolean;
  };
  reports: {
    reports_lapsed: boolean;
    reports_inventory_movement: boolean;
    reports_sales_graph: boolean;
  };
}

// Section name to key mapping
const SECTION_KEY_MAP: Record<string, keyof NestedPermissions> = {
  'Dashboard': 'dashboard',
  'Customers': 'customers',
  'Suppliers': 'suppliers',
  'Items': 'items',
  'Users': 'users',
  'Reports': 'reports',
};

/**
 * Convert flat permissions to nested structure for API
 */
export const toNestedPermissions = (flatPermissions: PagePermissions): NestedPermissions => {
  const nested: NestedPermissions = {
    dashboard: { view_dashboard: false },
    customers: {
      customers_list: false,
      customers_create: false,
      customers_edit: false,
      customers_orders: false,
      customers_order_view: false,
      customers_inbound_create: false,
      customers_outbound_create: false,
      shipments_list: false,
      shipments_view: false,
    },
    suppliers: {
      suppliers_list: false,
      suppliers_create: false,
      suppliers_edit: false,
      suppliers_orders: false,
      suppliers_order_view: false,
      suppliers_inbound_create: false,
    },
    items: {
      items_list: false,
      items_create: false,
      items_edit: false,
      items_detail: false,
      inventory_list: false,
    },
    users: {
      users_list: false,
      users_create: false,
      users_edit: false,
      users_item_limits: false,
    },
    reports: {
      reports_lapsed: false,
      reports_inventory_movement: false,
      reports_sales_graph: false,
    },
  };

  PERMISSION_SECTIONS.forEach(section => {
    const sectionKey = SECTION_KEY_MAP[section.name];
    if (sectionKey) {
      section.keys.forEach(key => {
        (nested[sectionKey] as Record<string, boolean>)[key] = flatPermissions[key] ?? false;
      });
    }
  });

  return nested;
};

/**
 * Convert nested permissions from API to flat structure
 */
export const fromNestedPermissions = (nested: NestedPermissions): PagePermissions => {
  const flat: PagePermissions = { ...DEFAULT_PAGE_PERMISSIONS };

  Object.values(nested).forEach(sectionPerms => {
    Object.entries(sectionPerms).forEach(([key, value]) => {
      if (key in flat) {
        flat[key as PagePermissionKey] = value as boolean;
      }
    });
  });

  return flat;
};

/**
 * Check if the permissions object is in nested format
 */
export const isNestedPermissions = (permissions: unknown): permissions is NestedPermissions => {
  if (typeof permissions !== 'object' || permissions === null) return false;
  return 'dashboard' in permissions || 'customers' in permissions || 'suppliers' in permissions;
};

/**
 * Normalize permissions from API - handles both flat and nested formats
 */
export const normalizePermissions = (permissions: unknown): PagePermissions => {
  if (!permissions) return { ...DEFAULT_PAGE_PERMISSIONS };
  
  if (isNestedPermissions(permissions)) {
    return fromNestedPermissions(permissions);
  }
  
  // Assume flat format, merge with defaults
  return { ...DEFAULT_PAGE_PERMISSIONS, ...(permissions as Partial<PagePermissions>) };
};

/**
 * Ordered list of main/list pages for redirect after login.
 * The first page the user has permission to access will be used as the default route.
 */
export const ORDERED_LIST_PAGES: { path: string; permissionKey: PagePermissionKey }[] = [
  { path: '/dashboard', permissionKey: 'view_dashboard' },
  { path: '/customers', permissionKey: 'customers_list' },
  { path: '/customers/orders', permissionKey: 'customers_orders' },
  { path: '/customers/shipments', permissionKey: 'shipments_list' },
  { path: '/suppliers', permissionKey: 'suppliers_list' },
  { path: '/suppliers/orders', permissionKey: 'suppliers_orders' },
  { path: '/items', permissionKey: 'items_list' },
  { path: '/items/stock', permissionKey: 'inventory_list' },
  { path: '/users', permissionKey: 'users_list' },
];

/**
 * Get the first accessible page for a user based on their permissions.
 * Returns the path of the first list page the user has permission to access.
 * Falls back to '/dashboard' if no page is accessible (shouldn't happen for valid users).
 */
export const getFirstAccessiblePage = (permissions: Partial<PagePermissions>): string => {
  for (const page of ORDERED_LIST_PAGES) {
    if (permissions[page.permissionKey] === true) {
      return page.path;
    }
  }
  // Fallback to dashboard
  return '/dashboard';
};
